(() => {
  "use strict";

  const ROOT = window.Shopify?.routes?.root || "/";
  const PAGE_BOX_SELECTOR = "[data-warranty-cart-page]";
  const state = {
    busy: false,
    integrityTimer: null,
    integrityBusy: false,
    layoutRaf: 0,
    opSeq: 0,
  };

  function beginCartMutation() {
    const next = Math.max(0, Number(window.__warrantyCartMutationSeq || 0)) + 1;
    window.__warrantyCartMutationSeq = next;
    return next;
  }

  function getCartMutationSeq() {
    return Math.max(0, Number(window.__warrantyCartMutationSeq || 0));
  }

  function isWarrantyLine(item) {
    return !!(item && item.properties && String(item.properties._is_warranty || "") === "1");
  }

  function num(value, fallback = 0) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  }

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function getPageSectionId() {
    return document.querySelector("cart-items-component[data-section-id]:not([data-drawer])")?.dataset.sectionId || null;
  }

  function getSectionsToRender() {
    const ids = new Set();

    document
      .querySelectorAll("cart-items-component[data-section-id]:not([data-drawer])")
      .forEach((el) => el.dataset.sectionId && ids.add(el.dataset.sectionId));

    return Array.from(ids);
  }

  function replaceSections(sections) {
    if (!sections || typeof sections !== "object") return false;

    let didReplace = false;

    Object.entries(sections).forEach(([sectionId, html]) => {
      if (!sectionId || !html) return;

      const current = document.getElementById(`shopify-section-${sectionId}`);
      if (!current) return;

      const parsed = new DOMParser().parseFromString(html, "text/html");
      const next = parsed.getElementById(`shopify-section-${sectionId}`);
      if (!next) return;

      current.replaceWith(next);
      didReplace = true;
    });

    return didReplace;
  }

  function captureAvailabilityWarnings() {
    return Array.from(document.querySelectorAll('[ref^="cartItemErrorContainer-"]')).map((container) => {
      if (!(container instanceof HTMLElement) || container.classList.contains('hidden')) return null;

      const refName = String(container.getAttribute('ref') || '');
      const match = refName.match(/cartItemErrorContainer-(\d+)/);
      const line = match ? Number(match[1]) : 0;
      if (!line) return null;

      const messageEl = container.querySelector(`[ref="cartItemError-${line}"]`);
      const quantityInput = document.querySelector(`input[data-cart-line="${line}"]`);

      return {
        line,
        message: messageEl?.textContent || '',
        quantity: quantityInput instanceof HTMLInputElement ? quantityInput.value : '',
      };
    }).filter(Boolean);
  }

  function restoreAvailabilityWarnings(snapshot) {
    (Array.isArray(snapshot) ? snapshot : []).forEach((entry) => {
      if (!entry || !entry.line) return;

      const container = document.querySelector(`[ref="cartItemErrorContainer-${entry.line}"]`);
      const messageEl = document.querySelector(`[ref="cartItemError-${entry.line}"]`);
      const quantityInput = document.querySelector(`input[data-cart-line="${entry.line}"]`);

      if (quantityInput instanceof HTMLInputElement && entry.quantity !== '') {
        quantityInput.value = String(entry.quantity);
        quantityInput.defaultValue = String(entry.quantity);
        quantityInput.setAttribute('value', String(entry.quantity));
        quantityInput.setAttribute('data-cart-quantity', String(entry.quantity));
      }

      if (container instanceof HTMLElement && messageEl instanceof HTMLElement && entry.message) {
        messageEl.textContent = entry.message;
        container.classList.remove('hidden');
      }
    });
  }

  function refreshHeaderState() {
    window.cancelAnimationFrame(state.layoutRaf);
    state.layoutRaf = window.requestAnimationFrame(() => {
      state.layoutRaf = window.requestAnimationFrame(() => {
        const header = document.querySelector("header-component");
        const headerGroup = document.querySelector("#header-group");
        const hasHeaderSection = headerGroup?.querySelector(".header-section");

        if (header && headerGroup) {
          const headerTopRow = header.querySelector(".header__row--top");
          const headerHeight = header.offsetHeight;

          let headerGroupHeight = 0;
          Array.from(headerGroup.children).forEach((element) => {
            if (!(element instanceof HTMLElement) || element === header) return;
            headerGroupHeight += element.offsetHeight;
          });

          if (header.hasAttribute("transparent") && header.parentElement?.nextElementSibling) {
            headerGroupHeight += headerHeight;
          }

          document.body.style.setProperty("--header-height", `${headerHeight}px`);
          document.body.style.setProperty("--header-group-height", `${headerGroupHeight}px`);

          if (headerTopRow) {
            document.body.style.setProperty("--top-row-height", `${headerTopRow.offsetHeight}px`);
          }

          if (!hasHeaderSection || !header.hasAttribute("transparent")) {
            document.body.style.setProperty("--transparent-header-offset-boolean", "0");
          } else {
            const hasImmediateSection = header.parentElement?.nextElementSibling?.classList.contains("shopify-section");
            document.body.style.setProperty(
              "--transparent-header-offset-boolean",
              hasImmediateSection ? "0" : "1"
            );
          }
        }

        const headerComponent = document.querySelector("#header-component");
        if (headerComponent) {
          const isTouchDevice = "ontouchstart" in window && navigator.maxTouchPoints > 0;
          const overflowList = headerComponent.querySelector("overflow-list");
          const hasReachedMinimum = overflowList?.hasAttribute("minimum-reached");
          headerComponent.dataset.menuStyle = isTouchDevice || hasReachedMinimum ? "drawer" : "menu";
        }

        window.dispatchEvent(new Event("resize"));
      });
    });
  }


  function setBusy(box, busy) {
    if (!box) return;
    box.classList.toggle("is-loading", !!busy);
    box.querySelectorAll("input, select, button").forEach((el) => {
      el.disabled = !!busy;
    });
  }

  async function cartAddWarranty(box, quantity) {
    const sections = getSectionsToRender();
    const payload = {
      items: [
        {
          id: num(box.dataset.warrantyVariantId, 0),
          quantity,
          properties: {
            _is_warranty: "1",
            _warranty_label: box.dataset.warrantyLabel || "36 Monate Langzeitgarantie",
            _warranty_details_url: box.dataset.warrantyDetailsUrl || "https://gasland24.de/pages/garantiebedingungen",
            _parent_variant_id: box.dataset.parentVariantId || "",
            _parent_line_key: box.dataset.parentLineKey || "",
            _parent_sku: box.dataset.parentSku || "",
            _parent_mpn: box.dataset.parentMpn || "",
          },
        },
      ],
      sections,
      sections_url: window.location.pathname,
    };

    const response = await fetch(`${ROOT}cart/add.js`, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(await response.text().catch(() => `cart/add.js failed (${response.status})`));
    }

    return response.json();
  }

  async function cartChangeWarranty(id, quantity) {
    const sections = getSectionsToRender();
    const payload = {
      id,
      quantity,
      sections: sections.join(","),
      sections_url: window.location.pathname,
    };

    const response = await fetch(`${ROOT}cart/change.js`, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(await response.text().catch(() => `cart/change.js failed (${response.status})`));
    }

    return response.json();
  }

  async function cartUpdate(updates) {
    const sections = getSectionsToRender();
    const payload = {
      updates,
      sections: sections.join(","),
      sections_url: window.location.pathname,
    };

    const response = await fetch(`${ROOT}cart/update.js`, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(await response.text().catch(() => `cart/update.js failed (${response.status})`));
    }

    return response.json();
  }

  async function getCart() {
    const response = await fetch(`${ROOT}cart.js`, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });

    if (!response.ok) {
      throw new Error(`cart.js failed (${response.status})`);
    }

    return response.json();
  }

  function getMainProductCountFromItems(items) {
    if (!Array.isArray(items)) return null;
    return items.reduce((sum, item) => sum + (isWarrantyLine(item) ? 0 : num(item.quantity, 0)), 0);
  }

  function syncBubbleFromCartItems(items) {
    const count = getMainProductCountFromItems(items);
    if (count == null) return;
    window.__warrantySetCartBubbleCount?.(count);
  }

  function notifyCartUpdated(data) {
    window.__warrantyNormalizeCartBubbleCount?.(true);
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        source: 'cart-page-warranty',
        data: {
          sections: data?.sections || {},
        },
      },
    }));
  }

  async function syncBox(box, quantity) {
    const parentQty = Math.max(1, num(box.dataset.parentQty, 1));
    const nextQty = clamp(num(quantity, 0), 0, parentQty);
    const warrantyKey = (box.dataset.warrantyKey || "").trim();
    const opSeq = ++state.opSeq;
    const mutationSeq = beginCartMutation();

    if (state.busy) return;
    state.busy = true;
    setBusy(box, true);

    try {
      const data =
        warrantyKey
          ? await cartChangeWarranty(warrantyKey, nextQty)
          : nextQty > 0
            ? await cartAddWarranty(box, nextQty)
            : null;

      if (opSeq !== state.opSeq) return;
      if (mutationSeq !== getCartMutationSeq()) return;

      const warningSnapshot = captureAvailabilityWarnings();

      if (data?.sections) {
        replaceSections(data.sections);
        restoreAvailabilityWarnings(warningSnapshot);
      }

      syncBubbleFromCartItems(data?.items);
      notifyCartUpdated(data);
    } catch (error) {
      console.error("[cart-page-warranty]", error);
      window.__warrantyNormalizeCartBubbleCount?.(true);
    } finally {
      state.busy = false;
      setBusy(box, false);
      scheduleIntegrityPass(80);
    }
  }

  async function integrityPass() {
    if (state.integrityBusy) return;
    state.integrityBusy = true;

    try {
      const cart = await getCart();
      const parentByKey = new Map();
      const parentByVariant = new Map();

      (cart.items || []).forEach((item) => {
        if (isWarrantyLine(item)) return;
        parentByKey.set(item.key, item);
        const variantId = String(item.variant_id || "");
        if (variantId && !parentByVariant.has(variantId)) {
          parentByVariant.set(variantId, item);
        }
      });

      const updates = {};

      (cart.items || []).forEach((item) => {
        if (!isWarrantyLine(item)) return;

        const parentLineKey = String(item.properties?._parent_line_key || "").trim();
        const parentVariantId = String(item.properties?._parent_variant_id || "").trim();

        let parent = null;
        if (parentLineKey && parentByKey.has(parentLineKey)) {
          parent = parentByKey.get(parentLineKey);
        } else if (parentVariantId && parentByVariant.has(parentVariantId)) {
          parent = parentByVariant.get(parentVariantId);
        }

        const currentQty = num(item.quantity, 0);
        const nextQty = parent ? clamp(currentQty, 0, num(parent.quantity, 0)) : 0;

        if (nextQty !== currentQty) {
          updates[item.key] = nextQty;
        }
      });

      if (Object.keys(updates).length) {
        const mutationSeq = beginCartMutation();
        const data = await cartUpdate(updates);
        if (mutationSeq !== getCartMutationSeq()) return;
        const warningSnapshot = captureAvailabilityWarnings();
        if (data?.sections) {
          replaceSections(data.sections);
          restoreAvailabilityWarnings(warningSnapshot);
        }
        syncBubbleFromCartItems(data?.items);
        notifyCartUpdated(data);
      }
    } catch (error) {
      console.error("[cart-page-warranty] integrity", error);
    } finally {
      state.integrityBusy = false;
    }
  }

  function scheduleIntegrityPass(delay = 180) {
    window.clearTimeout(state.integrityTimer);
    state.integrityTimer = window.setTimeout(() => {
      integrityPass();
    }, delay);
  }

  function onToggleChange(event) {
    const toggle = event.target.closest("[data-warranty-toggle]");
    if (!toggle) return;

    const box = toggle.closest(PAGE_BOX_SELECTOR);
    if (!box) return;

    const parentQty = Math.max(1, num(box.dataset.parentQty, 1));
    const select = box.querySelector("[data-warranty-qty]");
    const nextQty = toggle.checked ? parentQty : 0;

    if (select) {
      select.value = String(nextQty || 1);
    }

    syncBox(box, nextQty);
  }

  function onQtyChange(event) {
    const select = event.target.closest("[data-warranty-qty]");
    if (!select) return;

    const box = select.closest(PAGE_BOX_SELECTOR);
    if (!box) return;

    const nextQty = num(select.value, 0);
    const toggle = box.querySelector("[data-warranty-toggle]");
    if (toggle) {
      toggle.checked = nextQty > 0;
    }

    syncBox(box, nextQty);
  }



  function onStatusTriggerClick(event) {
    const trigger = event.target.closest('[data-warranty-status-trigger]');
    if (!trigger) return;

    const box = trigger.closest(PAGE_BOX_SELECTOR);
    if (!box) return;

    const toggle = box.querySelector('[data-warranty-toggle]');
    if (!toggle) return;

    event.preventDefault();
    toggle.checked = !toggle.checked;
    toggle.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function init() {
    if (!document.querySelector(PAGE_BOX_SELECTOR)) return;

    document.addEventListener("click", (event) => {
      if (event.target.closest('[data-warranty-status-trigger]')) {
        onStatusTriggerClick(event);
      }
    });

    document.addEventListener("keydown", (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.closest('[data-warranty-status-trigger]')) return;
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      onStatusTriggerClick(event);
    });

    document.addEventListener("change", (event) => {
      if (event.target.closest("[data-warranty-toggle]")) {
        onToggleChange(event);
      } else if (event.target.closest("[data-warranty-qty]")) {
        onQtyChange(event);
      }
    });

    document.addEventListener("cart:update", () => {
      scheduleIntegrityPass(80);
    });

    scheduleIntegrityPass(120);
  }

  document.addEventListener("DOMContentLoaded", init);
})();
